/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { TDictionary } from '../../../util/types';

type StatusDictionary = TDictionary<'idle' | 'pending' | 'fulfilled'>;

export interface SalesState {
  agreements: TDictionary<TDictionary<SalesAgreement>>;
  agreementsStatus: StatusDictionary;
  orders: TDictionary<SalesOrderProps[]>;
  orderStatus: StatusDictionary;
}

export interface SalesAgreementApiEntity {
  agreementId: string;
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  status: string;
  classification: string;
  deliveryMode?: string;
  carrier?: string;
  deliveryAddress?: string;
  deliveryAddressRecordId?: string;
  lines: SalesAgreementProductLine[];
  rawLines: SalesAgreementProductLine[];
  freightChargeUnitPrice?: number;
  freightChargeTotal?: number;
  productCostTotal?: number;
  totalCost?: number;
  supportsDelivery: boolean;
  deliveryRequiresReview: boolean;
  customerReference?: string;
}

export interface SalesAgreementProductLine {
  productVariantNumber: string;
  productDescription: string;
  scheduledDate: Date;
  quantity: {
    committed: number;
    released: number;
    delivered: number;
    invoiced: number;
    remaining: number;
  };
  isFreightItem: boolean;
  unit: string;
  unitPrice: number;
  netWeight?: number;
  startDate?: Date;
  endDate?: Date;
  tolerancePercentage: number;
  feedRate?: string;
  additives?: string;
}

export interface SalesAgreement extends SalesAgreementApiEntity {
  totalFulfilled: number;
  totalCommitted: number;
  totalReleased: number;
  totalInvoiced: number;
  percentageFulfilled: number;
  rawLines: SalesAgreementProductLine[];
  accountNumber: string;
}

export interface SalesLine {
  itemNumber: number;
  productVariantNumber: string;
  description: string;
  category: string;
  committed: number;
  released: number;
  delivered: number;
  invoiced: number;
  unit: string;
  unitPrice: number;
}

interface SalesOrderProductsEntity {
  productVariantNumber: string;
  productDescription: string;
  amount: number;
  unit: string;
}
export interface SalesOrderApiEntity {
  id: string;
  date: Date | string;
  status: string;
  products: SalesOrderProductsEntity[];
}

export interface SalesOrderApiResponse {
  orders: SalesOrderApiEntity[];
}

export type SalesAgreementApiResponse = { salesAgreements: SalesAgreementApiEntity[] };
export type SalesLineProps = SalesAgreementProductLine;
export type SalesAgreementProps = SalesAgreement;
export type SalesOrderProps = SalesOrderApiEntity;
export interface GetAgreementsReducerAction {
  data: TDictionary<SalesAgreement>;
  accountNumber: string;
}

export interface GetOrdersReducerAction {
  data: SalesOrderApiEntity[];
  agreementId: string;
}
